/**
 * Account overview dashboard — ONE contract shared by TekStak and the GAS
 * account dashboard, so "add the News widget and put it top-right" means
 * exactly the same thing in both shells.
 *
 * The account page used to be a fixed stack (TekStak: org chart + a frozen
 * sidebar; GAS: fourteen tabs). Everything a rep actually needed sat behind
 * a tab nobody opened. This turns the overview into a board the rep composes:
 * add a widget, drag it, size it, and the arrangement follows them.
 *
 * NO React here — this module is imported by server code (backend validation
 * mirror), by both clients, and by tests. The type -> component map lives in
 * each host's own registry, because the two hosts implement different subsets
 * (Value Pyramid is GAS-only; the Contact Inbox is TekStak-only).
 *
 * INVARIANT — an unknown widget type must never brick a board.
 * A config saved by a newer build, or by the other host, can name a type this
 * build has never heard of. Validation lets unknown `type` strings through on
 * purpose and the renderer shows a placeholder card. Pruning them would mean
 * that opening your board in the older shell silently deleted widgets.
 *
 * INVARIANT — layout is per (user, host), not per account.
 * You arrange the account page once; every account you open uses it. A
 * per-account layout sounds appealing and is a trap: it means account #2
 * onwards opens looking like the shipped default forever.
 *
 * Storage differs by host on purpose (same split as NavPrefs): TekStak keeps
 * the blob in `user_preferences.account_dashboard`, GAS in the scoped
 * `ACCOUNT_DASH` UserProperty. What is shared is the SHAPE.
 */

/**
 * Schema version. Each bump may carry a ONE-TIME, raise-only height migration;
 * see HEIGHT_MIGRATIONS. A board is stamped with the current version on read,
 * so every migration runs at most once per board and a height the user chooses
 * afterwards is theirs permanently.
 *
 * 1 -> 2: canvas widgets gained the `fill` flag. Heights saved at v1 were
 *   chosen while the org chart's canvas was pinned at 480px no matter how big
 *   its card was, so a small height there was not a preference — it was the
 *   user working around a bug.
 * 2 -> 3: the catalog's default heights were re-derived from MEASURED content
 *   across real accounts (identity 4 -> 12, news 9 -> 13, org-chart 20 -> 22,
 *   tasks 8 -> 9). Boards saved before that were sized against guesses, and a
 *   guess is not a preference either.
 * 3 -> 4: seven widgets were absorbed into siblings that answer the same
 *   question (see TYPE_MERGES). This one rewrites TYPES rather than heights,
 *   because the alternative is worse than doing nothing: an absorbed type is
 *   an unknown type, and unknown types are deliberately allowed through and
 *   rendered as a placeholder card. Deleting a catalog entry without this
 *   migration does not remove the widget from anyone's board — it replaces it
 *   with a permanent apology.
 * 4 -> 5: `pan-trajectory` leaves TekStak for `panw-heatmap`. Not a tidy-up —
 *   the trajectory was reading the SUPPLIER path, and Palo Alto never appears
 *   as a supplier in California procurement, so it rendered "No Palo Alto
 *   spend recorded" at an agency holding $13.2M of Palo Alto product. The
 *   merge is host-aware and lands on TekStak only; GAS keeps its own
 *   trajectory widget, which is still the right card there.
 * 5 -> 6: ORDINAL IDS. Widget ids were type-derived (`purchasing-authority-37`)
 *   and paid four times — once in `widgets`, once per breakpoint — so the id
 *   alone was ~60% of a full board's bytes and a 39-widget board broke Apps
 *   Script's 9,216-byte property ceiling with no cap left to raise. The catalog
 *   was FULL on 2026-09-11 for exactly that reason. This bump renumbers every
 *   widget `w1`, `w2`, … in stored order and remaps the layouts to match. It
 *   changes what an id IS, never where the widget sits: the layout entry
 *   follows the widget. See ORDINAL_ID_VERSION and the note on
 *   MAX_DASHBOARD_WIDGETS for the arithmetic.
 */
export const ACCOUNT_DASHBOARD_VERSION = 6;

/** The version at which ids became ordinal. Boards stamped below it are renumbered on read. */
export const ORDINAL_ID_VERSION = 6;

/**
 * Which widgets each version bump may raise. Raise-only, never past the
 * catalog default, and only for boards stamped below that version.
 *
 * Adding an entry is how a future default change reaches existing boards. It
 * is deliberately NOT a permanent floor: a floor would silently undo every
 * deliberate shrink forever, which is the difference between correcting a
 * guess once and overruling the user.
 */
/**
 * Widgets absorbed into a sibling, and the sibling that absorbed them. Every
 * pair here answers ONE question that the catalog was answering twice:
 * `pan-trajectory` was `spend-trajectory` with a filter applied, `contacts` was
 * the org chart with `view: 'list'`, and so on.
 *
 * REWRITE, never drop. The user put that widget on their board deliberately
 * and the capability still exists — it moved. Dropping it would read as the
 * board losing work, which is the thing a merge must not feel like.
 *
 * The exception is a board that already holds the survivor. Rewriting there
 * would leave two widgets of the same type, and the second one is not a second
 * opinion — it is the same card twice. Those are dropped, and the SURVIVOR's
 * own instance is the one kept, never whichever happened to be stored first:
 * it carries the size and position the user chose for the widget they are
 * actually keeping.
 */
const TYPE_MERGES: Record<string, AccountWidgetType> = {
  contacts: 'org-chart',
  // TekStak only, by construction: `panw-heatmap` is hosts: ['tekstak'], so
  // `mergeable()` refuses this pair on GAS and its trajectory card stays put.
  'pan-trajectory': 'panw-heatmap',
};

/**
 * What the absorbed widget was, expressed as the survivor's settings. Merging
 * `contacts` into People without this would move every flat-list widget on
 * every board to the org chart — the rep's card would still be there, showing
 * something they did not choose, which is a worse outcome than the duplicate
 * the merge exists to remove.
 */
const MERGE_CONFIG: Record<string, Record<string, unknown>> = {
  contacts: { view: 'list' },
};

/*
 * "Same subject" is not "same widget". Six other pairs each answer one question
 * the catalog answers twice, which is real, but a merge is only a catalog edit
 * when the SURVIVOR already does the absorbed widget's job. Otherwise it
 * deletes a capability instead of relocating it. Each is still worth doing;
 * none is free, and the work each needs is named here so the next session does
 * not re-derive it:
 *
 *   pan-trajectory -> spend-trajectory
 *     The catalog comment claims spend-trajectory "takes a vendor per
 *     instance". It does not — the registry maps them to fromDash('spend') and
 *     fromDash('pan'), and TEKSTAK_WIDGET_EDITORS holds only `tasks`. Merging
 *     turns everyone's PANW chart into an agency-wide one, silently.
 *     NEEDS: a vendor config on spend-trajectory, then carry { vendor } in the
 *     rewrite.
 *
 *   security-coverage -> coverage-ring      (both are hosts: BOTH)
 *   zero-trust        -> coverage-ring      (coverage-ring is ['tekstak'])
 *     NEEDS: coverage-ring registered on GAS, bundle rebuild, PANW paste.
 *
 *   meeting-notes -> meetings
 *     Not duplication. ['gas'] "summaries cleared for the tenant" vs
 *     ['tekstak']. The split IS the data boundary; collapsing them moves the
 *     boundary inside one widget's per-host behaviour. Leave it.
 *
 *   plaud-recordings -> meetings
 *     NEEDS: MeetingsWidget to grow the recordings list.
 *
 *   funding -> business-drivers
 *     Closest of the six — both fromIntel(), two sections of one panel. But
 *     AccountsOverviewIntel takes `section` as a single string.
 *     NEEDS: section to accept an array, plus an empty-state rule for when one
 *     section has content and the other does not.
 */

/**
 * Apply the merge map to a stored widget list. Returns the rewritten list;
 * layouts are reconciled by the caller, which already drops any layout item
 * whose widget id is gone.
 */
function applyTypeMerges(
  widgets: AccountWidgetInstance[],
  host: DashboardHost,
): AccountWidgetInstance[] {
  // A merge lands per HOST, because the catalog is per host. `contacts` folds
  // into the People widget on TekStak, where AccountsPeopleTab already carries
  // its own chart/list switch — but GAS renders the two from different
  // components and its org panel has no list mode, so there the absorbed
  // widget is still a real, separate capability and must be left alone.
  //
  // Reading that from the catalog rather than hard-coding it means this
  // function stays correct when a survivor later reaches the other shell: the
  // day coverage-ring is registered on GAS, its merges start applying there
  // with no change here.
  const mergeable = (from: string, to: AccountWidgetType) => {
    const target = ACCOUNT_WIDGET_META[to];
    if (!target || target.hosts.indexOf(host) === -1) return false;
    // Still in this host's catalog? Then it was never absorbed here.
    const source = ACCOUNT_WIDGET_META[from as AccountWidgetType];
    return !source || source.hosts.indexOf(host) === -1;
  };

  // A survivor already on the board wins over anything rewritten into it, in
  // whatever order they were stored.
  const nativeSurvivors = new Set(
    widgets.filter((w) => !(w.type in TYPE_MERGES)).map((w) => w.type as string),
  );
  const claimed = new Set<string>();
  const out: AccountWidgetInstance[] = [];
  for (const widget of widgets) {
    const target = TYPE_MERGES[widget.type as string];
    if (!target || !mergeable(widget.type as string, target)) {
      out.push(widget);
      continue;
    }
    if (nativeSurvivors.has(target) || claimed.has(target)) continue;
    claimed.add(target);
    // The id is kept so the board's existing layout entry still matches it and
    // the widget stays exactly where the user put it.
    //
    // CONFIG, not just type. An absorbed widget was usually its survivor with
    // something selected — `contacts` was the People widget opened on the flat
    // list — and rewriting the type alone silently changes what the rep sees.
    // `config` already exists on every instance and is already read by one
    // editor, so it is the right place to carry that intent across the merge.
    const carried = MERGE_CONFIG[widget.type as string];
    out.push(
      carried
        ? { ...widget, type: target, config: { ...(widget.config ?? {}), ...carried } }
        : { ...widget, type: target },
    );
  }
  return out;
}

const HEIGHT_MIGRATIONS: Array<{ version: number; applies: (meta: AccountWidgetMeta) => boolean }> = [
  // The org chart's canvas was pinned at 480px; any height chosen then was a
  // workaround, so only canvas widgets were touched.
  { version: 2, applies: (meta) => !!meta.fill },
  // Every default was a guess until it was measured, so this one is catalog-wide.
  { version: 3, applies: () => true },
];

/** Which shell can render a given widget. */
export type DashboardHost = 'tekstak' | 'gas';

export type AccountWidgetType =
  // --- identity / summary -------------------------------------------------
  | 'identity'
  | 'stats'
  | 'purchasing-authority'
  | 'locations'
  // --- people -------------------------------------------------------------
  | 'org-chart'
  | 'contacts'
  | 'contacts-directory'
  | 'competitive-footprint'
  | 'contact-inbox'
  // --- money / procurement ------------------------------------------------
  | 'renewals'
  | 'renewal-runway'
  | 'share-of-wallet'
  | 'buying-rhythm'
  | 'spend-trajectory'
  | 'pan-trajectory'
  | 'panw-heatmap'
  | 'procurement-summary'
  | 'purchase-history'
  | 'competitive'
  | 'security-coverage'
  | 'coverage-ring'
  | 'partners'
  | 'partner-involvement'
  | 'tech-map'
  | 'solicitations'
  | 'bcps'
  | 'zero-trust'
  // --- narrative ----------------------------------------------------------
  | 'news'
  | 'notes'
  | 'business-drivers'
  | 'funding'
  | 'value-pyramid'
  | 'trap-questions'
  // --- motion -------------------------------------------------------------
  | 'tasks'
  | 'opportunities'
  | 'meetings'
  | 'meeting-notes'
  | 'plaud-recordings'
  | 'deal-signals'
  | 'health-score'
  | 'proactive-questions'
  | 'commitments';

/** One placed widget. `id` is unique per board and is the grid item key. */
export interface AccountWidgetInstance {
  id: string;
  /** Deliberately a bare string at the type level for forward compatibility. */
  type: AccountWidgetType | string;
  /** Per-instance settings (a widget's own shape; opaque here). */
  config?: Record<string, unknown>;
}

/** react-grid-layout item, and the responsive map it round-trips. */
export interface DashboardGridItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardLayouts {
  lg?: DashboardGridItem[];
  md?: DashboardGridItem[];
  sm?: DashboardGridItem[];
}

export interface AccountDashboardConfig {
  v: number;
  widgets: AccountWidgetInstance[];
  layouts: DashboardLayouts;
}

/**
 * Catalog metadata. `deepHref`/`deepTab` is the widget-header "open the full
 * view" target — a widget is a doorway, never a dead end, so every entry
 * names where its full version lives.
 */
export interface AccountWidgetMeta {
  type: AccountWidgetType;
  title: string;
  /** One line for the add-widget catalog. */
  blurb: string;
  /** Catalog grouping. */
  /*
   * Catalog grouping — and, since a tab can be a board, the thing that decides
   * which widgets a tab offers.
   *
   * `Partners` was split out of `Money` when the Partners tab landed. `Money`
   * held 18 of 35 widgets and several were not money at all; a group that
   * means "everything about spend and everything near it" cannot pick out a
   * tab. Nothing stored moves: a board instance is `{ id, type, config }` and
   * carries no group, so re-tagging a widget changes only which section of the
   * add menu it appears under.
   */
  group: 'Account' | 'People' | 'Money' | 'Partners' | 'Narrative' | 'Motion';
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
  /** Shells that implement this widget. */
  hosts: DashboardHost[];
  /** TekStak route for "open full view" (omitted = no deeper view). */
  deepHref?: string;
  /** GAS account-tab key for "open full view". */
  deepTab?: string;
  /** Suppress the shell header in view mode (widgets that own their chrome). */
  chromeless?: boolean;
  /**
   * This widget DRAWS, it does not scroll: it should fill the cell rather than
   * scroll inside it. The shell gives its body a fill modifier and the
   * widget's own stylesheet flexes to that.
   *
   * Needed because a drawing typically pins its own height — the org chart's
   * `.oc-scroll` is `height: 480px`, and every chart was mounted at a literal
   * `height={220}` / `height={440}`. Without this, resizing the widget makes
   * the CARD bigger and leaves the drawing the same size, which reads as "the
   * widget is broken", not "the widget is small".
   *
   * Charts joined the canvases here in W1. Note the knock-on for the v1 -> v2
   * migration below, which raises `fill` widgets to the catalog default height
   * once: a board still on v1 now also has its chart widgets raised. That is
   * the same correction for the same reason (a height chosen while the drawing
   * was pinned at 220px was not a preference either), it only ever raises, and
   * it never exceeds the catalog default.
   */
  fill?: boolean;
}

/** Grid geometry. Shared so both hosts snap to the same cells. */
export const DASHBOARD_BREAKPOINTS = { lg: 1200, md: 820, sm: 0 } as const;
export const DASHBOARD_COLS = { lg: 12, md: 10, sm: 6 } as const;
export const DASHBOARD_ROW_HEIGHT = 40;
export const DASHBOARD_MARGIN: [number, number] = [14, 14];

export type DashboardBreakpoint = keyof typeof DASHBOARD_COLS;
export const DASHBOARD_BREAKPOINT_KEYS: DashboardBreakpoint[] = ['lg', 'md', 'sm'];

const BOTH: DashboardHost[] = ['tekstak', 'gas'];
const GAS_ONLY: DashboardHost[] = ['gas'];

/*
 * A NOTE ON minSize, because every entry carries the same floor and the reason
 * is one reason.
 *
 * `minSize` used to be a prohibition: a widget that could not render below four
 * columns declared four columns and the grid refused to go smaller. The TILE
 * RUNG retires that argument. Every widget in BOTH shells now draws something
 * deliberate at 102x95 — a figure where one is reachable, its mark and its name
 * where it is not — so a one-column cell is a card the reader chose, not a
 * broken one, and the floor can be one column everywhere.
 *
 * `h: 2` is the real floor, not `h: 1`. A single row is 40px, which fits a
 * figure or its label and not both. Widgets that asked for more height keep
 * whatever they asked for.
 *
 * This could not land per-widget. Every panel-mounting widget is `hosts:
 * BOTH`, so lowering TekStak's floors alone would have let a GAS user drag a
 * card to a size that shell could not draw. The floors came down when the
 * second registry could draw them, which is the same commit.
 */
export const ACCOUNT_WIDGET_META: Record<AccountWidgetType, AccountWidgetMeta> = {
  identity: {
    type: 'identity',
    title: 'Account identity',
    blurb: 'Logo, mandate, budget and headcount — who this agency is, in one band.',
    group: 'Account',
    // Measured, not guessed: the mandate paragraph plus wrapped fact chips
    // hit 585px on CDT. 4 rows clipped it on every account with a real
    // mandate, which is most of them.
    defaultSize: { w: 12, h: 12 },
    minSize: { w: 1, h: 6 },
    hosts: BOTH,
    chromeless: true,
  },
  stats: {
    type: 'stats',
    title: 'Key numbers',
    blurb: 'Spend, our share and renewing value — each with its trend.',
    group: 'Account',
    // h:4 not h:3 — the tiles carry a sparkline and a comparison now, not just
    // a numeral, and 3 rows clipped the trend line off the bottom.
    defaultSize: { w: 12, h: 4 },
    minSize: { w: 1, h: 3 },
    hosts: BOTH,
  },
  'purchasing-authority': {
    type: 'purchasing-authority',
    title: 'Purchasing authority',
    blurb: 'The DGS ceiling this account can sign to — and the buys that went over it.',
    group: 'Account',
    // Taller and `fill` since W6: the card is followed by a gauge plotting
    // their actual POs against the ceiling it names. The gauge is not a
    // separate widget on purpose — it is the PICTURE of what this card
    // already states in text, and splitting them would put the number and
    // its evidence in two cells a rep has to place next to each other by
    // hand. It also kept the catalog inside the widget cap, which the
    // headroom guard caught before this shipped.
    defaultSize: { w: 5, h: 10 },
    minSize: { w: 1, h: 5 },
    fill: true,
    hosts: BOTH,
  },
  locations: {
    type: 'locations',
    title: 'Locations',
    blurb: 'Every site this agency operates — offices, field offices, public counters — with a map pin Google and DGS agree on.',
    group: 'Account',
    defaultSize: { w: 6, h: 10 },
    // One column is enough for the tile: the figure is the staffed-site count,
    // with the address gap under it when there is one.
    minSize: { w: 1, h: 2 },
    // TekStak only until GAS registers it. GAS has the read-only Locations
    // panel (carve-out #12), but its widget registry has no entry yet and a
    // claim without a component renders a permanent "not available" card.
    hosts: ['tekstak'],
    // It DRAWS now — a map of the estate, not a list of rows — so it should
    // fill the cell rather than let its content decide the height. Without
    // this the body never flexes and a tall cell pushes the legend, the city
    // drill and the footnote straight out of the widget.
    //
    // Safe for saved boards: HEIGHT_MIGRATIONS are keyed to the version a
    // board was stored at, and the `fill` rung (version 2) has long since run
    // for every live board, so adding the flag now resizes nobody's layout.
    fill: true,
    deepTab: 'locations',
  },
  'org-chart': {
    type: 'org-chart',
    title: 'People',
    blurb: 'Who reports to whom, or the flat list — switch inside the widget.',
    group: 'People',
    // Big by default, and `fill` so the canvas actually uses the space. An org
    // chart at half height is unreadable — you can see three cards and a
    // scrollbar. Sized down from here is the rep's choice; sized down by
    // default just looks broken. 20 rows ≈ 1060px, which is Ryan's call after
    // seeing 16 on a real chart (2026-09-09) — deep trees need the room, and
    // this is the one widget people work IN rather than glance at.
    defaultSize: { w: 12, h: 22 },
    minSize: { w: 1, h: 10 },
    fill: true,
    hosts: BOTH,
    deepTab: 'org',
  },
  contacts: {
    type: 'contacts',
    // GAS-only since the People merge. On TekStak this widget WAS the org
    // chart with `view: 'list'` — two catalog slots for one component's two
    // props — and AccountsPeopleTab already renders its own chart/list switch
    // when no view is forced, so the merged People widget gives a rep both.
    // GAS is different: its org panel and its contact list are separate
    // components and the panel has no list mode, so here the flat list is
    // still a real capability. It folds in when GAS's panel grows a toggle.
    //
    // TekStak's searchable directory is `contacts-directory`, a DIFFERENT
    // type. Re-enabling this one here would have un-absorbed the merge — see
    // the note on that entry.
    title: 'Contacts',
    blurb: 'Every known person at this account as a flat, scannable list.',
    group: 'People',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: GAS_ONLY,
    // Neither shell has a standalone Contacts tab any more — GAS never did and
    // TekStak merged it into People — so both open the combined one.
    deepTab: 'org',
  },
  'contacts-directory': {
    type: 'contacts-directory',
    /*
     * A NEW TYPE, NOT `contacts` RE-ENABLED, and the difference matters.
     *
     * `contacts` is a recorded merge: on TekStak it WAS `org-chart` with
     * `view: 'list'`, and TYPE_MERGES rewrites it on read. Setting its hosts
     * back to BOTH un-absorbs that merge — `mergeable()` reads the catalog, so
     * a type back in the host's catalog stops being rewritten — which silently
     * changes what every pre-merge saved board renders and guts the guard on
     * the merge mechanism itself. The slot it would have saved is not scarce:
     * the catalog sits well inside its two-spare headroom.
     *
     * And it is genuinely a different widget. `contacts` was a rendering MODE
     * of the org chart; this searches. A scannable list of 39 people is one a
     * rep reads with Cmd-F, which is the gap it closes.
     */
    title: 'Contacts directory',
    blurb: 'Search every known person at this account — by name, title or email.',
    group: 'People',
    defaultSize: { w: 6, h: 12 },
    minSize: { w: 1, h: 5 },
    hosts: ['tekstak'],
    deepTab: 'org',
  },
  'contact-inbox': {
    type: 'contact-inbox',
    title: 'Contact inbox',
    blurb: 'Harvested people waiting to be claimed or dismissed — all accounts.',
    group: 'People',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: ['tekstak'],
    deepHref: '/crm',
  },
  renewals: {
    type: 'renewals',
    title: 'Renewals',
    blurb: "The account's clock — expiring paper, competitor security first.",
    group: 'Money',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'procurement',
  },
  'renewal-runway': {
    type: 'renewal-runway',
    title: 'Renewal runway',
    blurb: 'Every expiring contract on one timeline — value, urgency, ours vs theirs.',
    group: 'Money',
    // Wider than tall by default: it is a TIME axis, and a runway squeezed
    // into a narrow cell puts two years of expiries inside 200px.
    defaultSize: { w: 8, h: 9 },
    minSize: { w: 1, h: 2 },
    fill: true,
    // TekStak-only for now — the chart itself is in @techstack/ui, so GAS is a
    // registry entry and a bundle rebuild away, not a port.
    hosts: ['tekstak'],
    deepTab: 'procurement',
  },
  'share-of-wallet': {
    type: 'share-of-wallet',
    title: 'Share of wallet',
    blurb: 'Who owns this account\u2019s spend, by area — ours against everyone else.',
    group: 'Money',
    defaultSize: { w: 4, h: 9 },
    minSize: { w: 1, h: 2 },
    fill: true,
    hosts: ['tekstak'],
    deepTab: 'procurement',
  },
  'buying-rhythm': {
    type: 'buying-rhythm',
    title: 'Buying rhythm',
    blurb: 'Which months this agency actually signs in, year over year.',
    group: 'Money',
    // Twelve columns want width; five rows of squares want very little height.
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 1, h: 2 },
    fill: true,
    hosts: ['tekstak'],
    deepTab: 'procurement',
  },
  'spend-trajectory': {
    type: 'spend-trajectory',
    title: 'Spend trajectory',
    blurb: 'What this agency spends, fiscal year by fiscal year.',
    group: 'Money',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 6 },
    fill: true,
    hosts: BOTH,
    deepTab: 'procurement',
  },
  'pan-trajectory': {
    type: 'pan-trajectory',
    title: 'Palo Alto trajectory',
    blurb: 'PANW spend at this agency, year by year on your fiscal calendar.',
    group: 'Money',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 6 },
    fill: true,
    // GAS-only now. On TekStak this is absorbed by `panw-heatmap` (see
    // TYPE_MERGES), because the trajectory reads the SUPPLIER path and Palo
    // Alto has no supplier rows in California procurement at all. GAS keeps
    // it: its own chart is bucketed on the viewer's fiscal basis and pairs
    // with the agency-wide trend beside it, and replacing that is a GAS
    // change with a bundle rebuild and a tenant paste behind it.
    hosts: ['gas'],
    deepTab: 'procurement',
  },
  'panw-heatmap': {
    type: 'panw-heatmap',
    title: 'Palo Alto footprint',
    blurb: 'Which Palo Alto product areas land here, year by year — and who sells them.',
    group: 'Money',
    /*
     * What `pan-trajectory` should have been, and the reason it is a different
     * widget rather than a fix.
     *
     * The trajectory asked "how much Palo Alto does this agency buy" and
     * answered via the SUPPLIER. Palo Alto never appears as a supplier in
     * California procurement — it sells entirely through the channel — so the
     * card read "No Palo Alto spend recorded at this agency" at CDT, which
     * holds $13.2M of Palo Alto product bought through NWN Solutions,
     * Enterprise Networking Solutions and OPTM West.
     *
     * This reads the PRODUCT path (`tech_components.vendor_name`) on Ryan's
     * call, and carries the channel on its face: a heatmap of security
     * category x fiscal year, with the resellers named underneath. That is a
     * wider claim than the old one and the widget says which claim it is
     * making — "product bought", not "revenue".
     *
     * A time axis with a dozen category rows wants width and height; the
     * trajectory's 6x9 was sized for bars.
     */
    defaultSize: { w: 8, h: 10 },
    minSize: { w: 1, h: 2 },
    fill: true,
    hosts: ['tekstak'],
    deepTab: 'procurement',
  },
  'procurement-summary': {
    type: 'procurement-summary',
    title: 'Procurement summary',
    blurb: 'Total spend with the top vendors and categories behind it.',
    group: 'Money',
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'procurement',
  },
  'purchase-history': {
    type: 'purchase-history',
    title: 'Purchase history',
    blurb: 'What they have been buying, newest first — with the cyber share of each.',
    group: 'Money',
    /*
     * A new type rather than a rewrite of `procurement-summary`, and that was
     * not the first plan.
     *
     * Taking that slot would have been free — it is the worst widget on the
     * board (prose, every vendor rendered `$0`, zero click targets, overflows
     * its cell) and `competitive` set the precedent for replacing what a widget
     * draws while keeping its type. But `procurement-summary` is `hosts: BOTH`,
     * and GAS renders its own `WProcurementSummary` under that type. Retitling
     * the shared entry would have put "Purchase history" in the GAS picker in
     * front of a procurement summary — the exact cross-host mistake that nearly
     * took security coverage off GAS in the merge pass.
     *
     * So this is TekStak-only and costs the catalog's last TekStak slot:
     * claimed goes 33 -> 34 against a guard of MAX_DASHBOARD_WIDGETS - 2 = 34.
     * It fits with nothing to spare, which is a real debt and not a comfortable
     * place to leave the catalog. The clean end state is retiring
     * `procurement-summary` from TekStak's hosts once the reworked cut pass
     * lands — that hands the slot straight back, and it needs the same
     * rewrite-with-config migration `contacts` got, scoped so GAS keeps the
     * summary it still renders.
     *
     * Taller and wider than a summary: a feed needs rows, and each row carries
     * a supplier, a size bar and a cyber share.
     */
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 1, h: 2 },
    hosts: ['tekstak'],
    deepTab: 'procurement',
  },
  competitive: {
    type: 'competitive',
    title: 'Gaining and losing',
    blurb: 'Vendor rank year over year — who is climbing at this account, and who is falling.',
    group: 'Money',
    // W7: this widget stopped mounting the whole Competitive TAB and became a
    // bump chart, which is the competitive question a rep actually carries —
    // "compared to whom", not "how much". Wider and `fill` accordingly: it is
    // a time axis whose right-edge name labels are what make a legend
    // unnecessary. The tab behind the header stays the full view.
    //
    // Not a new catalog type on purpose. The widget cap is the binding
    // constraint (see MAX_DASHBOARD_WIDGETS) and the bump is not a second
    // subject — it is the better answer to this widget's own question.
    defaultSize: { w: 8, h: 9 },
    minSize: { w: 1, h: 6 },
    fill: true,
    hosts: BOTH,
    deepTab: 'competitive',
  },
  'security-coverage': {
    type: 'security-coverage',
    title: 'Security coverage',
    blurb: 'Which security categories they pay for, and who owns each one.',
    group: 'Money',
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 1, h: 6 },
    fill: true,
    hosts: BOTH,
    deepTab: 'zt',
  },
  'coverage-ring': {
    type: 'coverage-ring',
    title: 'Coverage gaps',
    blurb: 'The 14 security categories as a wheel — who holds each, and which nobody does.',
    group: 'Money',
    /*
     * Squarer than the other Money widgets: it is a wheel plus a short list,
     * not a time series, so extra WIDTH buys it nothing after a point.
     *
     * SIX, not four (Ryan, 2026-09-18: "the labels come and go as the dash
     * gets resized"). That point is above 399px, because `widgets.css` drops
     * all thirteen segment labels below it — thirteen names round a small
     * wheel collide rather than shrink. At w:4 the container lands at 270-360px
     * across ordinary dash widths, straddling the threshold, so the labels
     * blinked in and out as the window moved. At w:6 it clears 399px at every
     * breakpoint down to 820px, and below that the widget takes the full row
     * anyway. The extra width buys exactly one thing, and it is the labels.
     */
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 2 },
    fill: true,
    hosts: ['tekstak'],
    deepTab: 'zt',
  },
  partners: {
    type: 'partners',
    title: 'Partner spend by category',
    blurb: 'Which resellers carry Palo Alto into this account, split by security category.',
    group: 'Partners',
    defaultSize: { w: 6, h: 12 },
    minSize: { w: 1, h: 8 },
    fill: true,
    hosts: BOTH,
    deepTab: 'competitive',
  },
  'partner-involvement': {
    type: 'partner-involvement',
    title: 'Partner involvement',
    blurb: 'Who sells Palo Alto into this account — on ETC, SLP or neither — and what else they sell here, competing security first.',
    group: 'Partners',
    /*
     * SEVEN ROWS, NOT TEN. A grid cell is a fixed height and this widget's
     * content is not: ten rows is 400px, and a typical account has THREE
     * partners (CDT 3, FTB 2, Secretary of State 3), which left most of the
     * cell empty above the next widget. Seven fits four or five partners; past
     * that the body scrolls, which it already did. A rep with a fat reseller
     * list drags it taller once — better than every other rep starting with a
     * hole in the page.
     */
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 1, h: 6 },
    // TekStak first; GAS keeps the slot as a placeholder until it registers
    // the widget (an unknown type never bricks a board).
    hosts: ['tekstak'],
    deepTab: 'competitive',
  },
  'competitive-footprint': {
    type: 'competitive-footprint',
    /*
     * EXISTS SO THE COMPETITIVE TAB CAN BECOME A BOARD WITHOUT LOSING ANYTHING.
     *
     * Every other widget that names `competitive` as its full view answers a
     * different question — `competitive` ranks vendors over time, the two
     * partner widgets ask who resells. The tab's own footprint sections run
     * `accounts-competitive-footprint`, and NO widget used that query: making
     * the tab a board without this would have quietly deleted the competitor
     * footprint, which is the failure mode that kept eight one-widget tabs as
     * panels. So the panel becomes a widget, the same move `plaud-recordings`
     * already makes for the Meetings tab.
     */
    title: 'Competitor footprint',
    blurb: 'Ours against theirs at this account — spend trend, category split and who holds what.',
    group: 'Money',
    defaultSize: { w: 12, h: 16 },
    minSize: { w: 1, h: 8 },
    hosts: ['tekstak'],
    deepTab: 'competitive',
  },
  'tech-map': {
    type: 'tech-map',
    title: 'Tech map',
    blurb: 'The estate as a canvas — what they run, drawn from your own notes.',
    group: 'Money',
    defaultSize: { w: 12, h: 12 },
    minSize: { w: 1, h: 7 },
    hosts: BOTH,
    deepTab: 'techmap',
  },
  solicitations: {
    type: 'solicitations',
    title: 'Open solicitations',
    blurb: 'RFPs and bids this agency has out, soonest close first.',
    group: 'Money',
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'solicitations',
  },
  bcps: {
    type: 'bcps',
    title: 'Budget change proposals',
    blurb: 'What they asked the Legislature to fund — money before it is money.',
    group: 'Money',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'budget',
  },
  'zero-trust': {
    type: 'zero-trust',
    title: 'Zero Trust posture',
    blurb: 'The account’s Zero Trust score and where the gaps sit.',
    group: 'Money',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'zt',
  },
  news: {
    type: 'news',
    title: 'News',
    blurb: 'Press and internal signals tagged to this agency, newest first.',
    group: 'Narrative',
    // 681px measured at the 5 stories the board asks for.
    defaultSize: { w: 6, h: 13 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepHref: '/news',
    deepTab: 'news',
  },
  notes: {
    type: 'notes',
    title: 'Notes',
    blurb: 'Your customer messaging notes for this account.',
    group: 'Narrative',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
    deepTab: 'notes',
  },
  'business-drivers': {
    type: 'business-drivers',
    title: 'Drivers & objectives',
    blurb: 'What is pushing them this year, and the talk tracks that land.',
    group: 'Narrative',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: BOTH,
  },
  funding: {
    type: 'funding',
    title: 'Funding model',
    blurb: 'Appropriated or revenue-generating — how their money arrives.',
    group: 'Narrative',
    defaultSize: { w: 4, h: 6 },
    minSize: { w: 1, h: 3 },
    hosts: BOTH,
  },
  'value-pyramid': {
    type: 'value-pyramid',
    title: 'Value pyramid',
    blurb: 'The four-tier value map and the golden thread through it.',
    group: 'Narrative',
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 1, h: 7 },
    hosts: BOTH,
    deepTab: 'pyramid',
  },
  'trap-questions': {
    type: 'trap-questions',
    title: 'Trap questions',
    blurb: 'Competitive questions to set, tuned to what they already own.',
    group: 'Narrative',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 6 },
    hosts: BOTH,
    deepTab: 'traps',
  },
  tasks: {
    type: 'tasks',
    title: 'Open tasks',
    blurb: 'What you owe this account, overdue first.',
    group: 'Motion',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 2 },
    // TekStak-only: the GAS dashboard has no account-scoped task source —
    // its Tasks view is the whole funnel, not this agency's slice. Better
    // absent from the catalog than present and always empty.
    hosts: ['tekstak'],
    deepHref: '/tasks',
  },
  opportunities: {
    type: 'opportunities',
    title: 'Pipeline',
    blurb: 'Open opportunities and what each is worth.',
    group: 'Motion',
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 1, h: 2 },
    hosts: ['tekstak'],
    deepHref: '/pipeline',
  },
  meetings: {
    type: 'meetings',
    title: 'Meetings',
    blurb: 'Upcoming and recent meetings with this account.',
    group: 'Motion',
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 1, h: 2 },
    // TekStak-only; GAS's equivalent is the shared meeting-notes surface,
    // which is a different thing (tenant-cleared summaries, not a calendar).
    hosts: ['tekstak'],
    deepTab: 'meetings',
  },
  'meeting-notes': {
    type: 'meeting-notes',
    title: 'Meeting notes',
    blurb: 'Shared meeting summaries cleared for the tenant.',
    group: 'Motion',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: ['gas'],
    deepTab: 'meetings',
  },
  'plaud-recordings': {
    type: 'plaud-recordings',
    title: 'Recordings',
    blurb: 'Plaud recordings for this account, with transcripts and summaries.',
    group: 'Motion',
    defaultSize: { w: 6, h: 9 },
    minSize: { w: 1, h: 5 },
    hosts: ['tekstak'],
    deepTab: 'meetings',
  },
  'deal-signals': {
    type: 'deal-signals',
    title: 'Deal signals',
    blurb: 'Where to focus — signals pulled out of your notes and activity.',
    group: 'Motion',
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 1, h: 4 },
    hosts: ['gas'],
  },
  'health-score': {
    type: 'health-score',
    title: 'Account health',
    blurb: 'A single score for how this relationship is actually doing.',
    group: 'Motion',
    defaultSize: { w: 4, h: 7 },
    minSize: { w: 1, h: 4 },
    hosts: ['tekstak'],
  },
  'proactive-questions': {
    type: 'proactive-questions',
    title: 'Questions to ask',
    blurb: 'Gaps in what you know, phrased as questions for the next call.',
    group: 'Motion',
    defaultSize: { w: 4, h: 7 },
    minSize: { w: 1, h: 4 },
    hosts: ['tekstak'],
  },
  commitments: {
    type: 'commitments',
    title: 'Commitments',
    blurb: 'What you promised them and what they promised you.',
    group: 'Motion',
    defaultSize: { w: 4, h: 7 },
    minSize: { w: 1, h: 4 },
    hosts: ['tekstak'],
  },
};

export const ACCOUNT_WIDGET_TYPES = Object.keys(ACCOUNT_WIDGET_META) as AccountWidgetType[];

/** The catalog for one shell, in stable catalog order. */
export function widgetsForHost(host: DashboardHost): AccountWidgetMeta[] {
  return ACCOUNT_WIDGET_TYPES
    .map((type) => ACCOUNT_WIDGET_META[type])
    .filter((meta) => meta.hosts.indexOf(host) !== -1);
}

export function widgetMeta(type: string): AccountWidgetMeta | null {
  return Object.prototype.hasOwnProperty.call(ACCOUNT_WIDGET_META, type)
    ? ACCOUNT_WIDGET_META[type as AccountWidgetType]
    : null;
}

/**
 * Build a layout map from a simple ordered spec, so the shipped defaults are
 * readable and cannot drift into overlaps. Widgets flow left-to-right and wrap;
 * a widget wider than the remaining space starts a new row.
 *
 * Hand-writing x/y for three breakpoints is how a default layout silently ends
 * up overlapping — the compactor then shuffles it rather than complaining, and
 * the shipped first-run board looks nothing like what was intended.
 */
export function packLayouts(
  widgets: Array<{ id: string; type: string }>,
): DashboardLayouts {
  const layouts: DashboardLayouts = {};
  for (const bp of DASHBOARD_BREAKPOINT_KEYS) {
    const cols = DASHBOARD_COLS[bp];
    const items: DashboardGridItem[] = [];
    let x = 0;
    let y = 0;
    let rowHeight = 0;
    for (const widget of widgets) {
      const meta = widgetMeta(widget.type);
      const size = meta ? meta.defaultSize : { w: 6, h: 8 };
      const minW = meta ? meta.minSize.w : 3;
      /*
       * `defaultSize.w` is expressed in LG columns (12). Carrying that number
       * verbatim onto a narrower grid is what stopped half-width widgets
       * pairing up: at md the grid is 10 columns wide, two w:6 widgets asked
       * for 12, and the second wrapped onto its own row. A rep on a 1280px
       * laptop got a single column of full-width cards with the page's whole
       * right half empty — which is the awkward whitespace, not the widgets
       * being too tall.
       *
       * Scaling the SHARE instead keeps "half the board" meaning half the
       * board at every width: 6-of-12 becomes 5-of-10, and the pair fits.
       */
      const scaled = Math.round((size.w / DASHBOARD_COLS.lg) * cols);
      const w = Math.max(Math.min(minW, cols), Math.min(scaled, cols), 1);
      const full = bp === 'sm' ? cols : w;
      if (x + full > cols) {
        x = 0;
        y += rowHeight;
        rowHeight = 0;
      }
      items.push({ i: widget.id, x, y, w: full, h: size.h });
      x += full;
      rowHeight = Math.max(rowHeight, size.h);
      if (x >= cols) {
        x = 0;
        y += rowHeight;
        rowHeight = 0;
      }
    }
    layouts[bp] = items;
  }
  return layouts;
}

function makeDefault(host: DashboardHost, types: AccountWidgetType[]): AccountDashboardConfig {
  const widgets = types
    .filter((type) => ACCOUNT_WIDGET_META[type].hosts.indexOf(host) !== -1)
    .map((type, i) => ({ id: ordinalId(i + 1), type }));
  return { v: ACCOUNT_DASHBOARD_VERSION, widgets, layouts: packLayouts(widgets) };
}

/**
 * First-run boards. Deliberately NOT "every widget" — a wall of thirty cards
 * is the same problem as fourteen tabs. These are the ones a rep opens the
 * account page for; everything else is one click away in the catalog.
 */
export function defaultAccountDashboard(host: DashboardHost): AccountDashboardConfig {
  return host === 'gas'
    ? makeDefault(host, [
        'identity',
        'purchasing-authority',
        'spend-trajectory',
        'pan-trajectory',
        'renewals',
        'news',
        'security-coverage',
        'business-drivers',
        'procurement-summary',
        'notes',
      ])
    // TekStak mirrors the GAS list above, in the same order, because the whole
    // point of the parity work was that this page should show what the GAS
    // account dashboard shows — Ryan, 2026-09-09: "all tabs, all graphs the
    // same". The shipped board was missing every chart: no spend trajectory,
    // no PANW trajectory, no security coverage, no identity band. They were in
    // the catalog but a rep had to go and add each one.
    //
    // Then the two TekStak has that GAS does not, kept because they are why
    // this page gets opened: the org chart (Ryan wanted it ON this page, not
    // split off) and the account's open tasks.
    : makeDefault(host, [
        // MERGE NOTE — two different boards met here, both Ryan's:
        //   main:  the GAS-parity list ("all tabs, all graphs the same",
        //          2026-09-09), which is what ships today.
        //   W6:    a shorter board where "the drawn versions lead".
        // Naively keeping either side loses a real intent, and keeping both
        // listed org-chart twice — makeDefault does not dedupe, so that would
        // have produced two widgets sharing the id 'org-chart'.
        //
        // Resolved as parity's LIST with W6's SUBSTITUTIONS: same widgets in
        // the same order as GAS, except that where a drawn version of a panel
        // now exists it takes the slot — renewals -> renewal-runway,
        // procurement-summary -> share-of-wallet. Parity is about which
        // subjects appear; "drawn leads" is about how each one is rendered.
        // They were never actually in conflict.
        'identity',
        'purchasing-authority',
        'spend-trajectory',
        'pan-trajectory',
        'renewal-runway',
        'news',
        'security-coverage',
        'business-drivers',
        'share-of-wallet',
        'notes',
        'org-chart',
        'tasks',
      ]);
}

/* ── Boards ────────────────────────────────────────────────────────────────
 *
 * A tab IS a board. The Dash proved the construct — add a widget, drag it,
 * size it, remove it — and the rest of the account page is still a fixed panel
 * per tab. This is what lets a second tab be composed the same way.
 *
 * ONE PREFERENCE PER BOARD, not one blob holding all of them. The 8800-byte
 * cap exists because GAS stores a board in a single Apps Script property and
 * those cap at 9KB; three boards in one property would blow that ceiling the
 * day a rep filled them. Separate keys also mean a corrupt People board cannot
 * take the Dash down with it.
 *
 * The overview keeps the ORIGINAL preference type. Every board arranged before
 * this existed lives under `account_dashboard`, and renaming it would silently
 * reset every rep to the shipped default.
 */

export type AccountBoardKey =
  | 'overview' | 'people' | 'partners' | 'procurement' | 'competitive' | 'zt' | 'meetings';

export interface AccountBoardMeta {
  key: AccountBoardKey;
  /** The account tab this board renders as. */
  tab: string;
  title: string;
  /**
   * Catalog groups this board's ADD MENU offers. `null` offers everything.
   *
   * It filters the menu and NOTHING else: a saved board keeps every widget it
   * holds, whatever group it came from. That is the same rule role and feature
   * gates already follow here, and for the same reason — a board arranged in
   * another shell, or before a group existed, must survive being opened.
   */
  groups: AccountWidgetMeta['group'][] | null;
  /**
   * An explicit allowlist, which WINS over `groups` when present.
   *
   * Groups are the right selector when a tab and a group are the same subject,
   * as People and Partners are. They are the wrong one for Procurement:
   * `Money` holds fifteen TekStak widgets spread across six different tabs —
   * procurement, zero trust, tech map, solicitations, BCPs and competitive —
   * so a Money board would offer a rep the Zero Trust posture card while they
   * are reading purchase orders. Splitting `Money` five ways to fix that would
   * be a taxonomy change made to satisfy a menu.
   */
  types?: AccountWidgetType[];
  /** First-run widgets, per host. Filtered by `hosts` like any default. */
  defaults: Record<DashboardHost, AccountWidgetType[]>;
}

export const ACCOUNT_BOARDS: Record<AccountBoardKey, AccountBoardMeta> = {
  overview: {
    key: 'overview',
    tab: 'overview',
    title: 'Dash',
    groups: null,
    defaults: { tekstak: [], gas: [] },
  },
  procurement: {
    key: 'procurement',
    tab: 'procurement',
    title: 'Procurement',
    /*
     * The eight widgets that already name this tab as their full view — which
     * is the definition of "belongs here" the catalog has carried all along.
     *
     * The tab it replaces ran exactly two queries, `accounts-renewals` and
     * `accounts-procurement-summary`, and both are widgets on this board (the
     * panel's own comment says it shares the Renewals widget's key so opening
     * the tab is a cache hit). So nothing is lost and six more arrive, several
     * of them the DRAWN versions of what that panel listed.
     */
    groups: null,
    types: [
      'purchase-history',
      'renewal-runway',
      'renewals',
      'share-of-wallet',
      'buying-rhythm',
      'spend-trajectory',
      'panw-heatmap',
      'procurement-summary',
    ],
    defaults: {
      // Drawn versions lead, the same resolution the Dash default settled on:
      // renewal-runway over renewals, share-of-wallet over procurement-summary.
      tekstak: ['purchase-history', 'renewal-runway', 'share-of-wallet', 'buying-rhythm'],
      gas: ['purchase-history', 'renewals', 'procurement-summary'],
    },
  },
  /*
   * The three tabs whose widgets outnumber one. Each board's `types` is the
   * set that already names that tab as its full view — the catalog's own
   * definition of "belongs here" — and each panel survives as a widget, so no
   * tab loses what it rendered.
   */
  competitive: {
    key: 'competitive',
    tab: 'competitive',
    title: 'Competitive',
    groups: null,
    types: ['competitive', 'competitive-footprint', 'partner-involvement', 'partners'],
    defaults: {
      // The footprint leads because it IS the tab this replaces; the bump
      // chart is the question a rep carries on top of it.
      tekstak: ['competitive-footprint', 'competitive'],
      gas: ['competitive', 'partners'],
    },
  },
  zt: {
    key: 'zt',
    tab: 'zt',
    title: 'Zero trust',
    groups: null,
    types: ['zero-trust', 'security-coverage', 'coverage-ring'],
    /*
     * `zero-trust` mounts the WHOLE ZeroTrust component — 138 files of CISA
     * maturity model and assessments — so it is the tab, not a summary of it,
     * and it leads at full width. The other two are the readings a rep wants
     * beside it rather than behind another click.
     */
    defaults: {
      tekstak: ['zero-trust', 'security-coverage', 'coverage-ring'],
      gas: ['zero-trust', 'security-coverage'],
    },
  },
  meetings: {
    key: 'meetings',
    tab: 'meetings',
    title: 'Meeting notes',
    groups: null,
    // `meeting-notes` is GAS's own; leaving it out made this board offer
    // NOTHING there and open empty — a Customize button over a blank menu.
    // The rule is the tab a widget names, not the shell it happens to run in.
    types: ['plaud-recordings', 'meetings', 'meeting-notes'],
    // `plaud-recordings` is `fromTab(AccountsMeetingsTab)` — the tab itself,
    // already a widget before this slice.
    defaults: {
      tekstak: ['plaud-recordings', 'meetings'],
      gas: ['meeting-notes'],
    },
  },
  partners: {
    key: 'partners',
    tab: 'partners',
    title: 'Partners',
    groups: ['Partners'],
    /*
     * Two widgets, and deliberately not padded out with spend charts that
     * merely mention a reseller. The question this tab answers is "who carries
     * us in here, and on what paper" — `partner-involvement` is that answer and
     * `partners` is the same money split by what it bought. Anything else a rep
     * wants is one click away in the add menu.
     *
     * `partners` LEADS (Ryan, 2026-09-18). The shape of the account comes
     * first — how much went through resellers and into which categories — and
     * the named partners, their vehicles and their contract ids are the detail
     * you read second. On lg and md the two sit side by side, so "first" is
     * the left-hand column.
     *
     * `partner-involvement` is TekStak-only, so GAS opens on the one it has;
     * `makeDefault` drops what a host cannot render.
     */
    defaults: {
      tekstak: ['partners', 'partner-involvement'],
      gas: ['partners'],
    },
  },
  people: {
    key: 'people',
    tab: 'org',
    title: 'People',
    groups: ['People'],
    /*
     * The org chart leads because it is the widget people work IN rather than
     * glance at, and it already carries the flat-list switch inside itself —
     * so this board opens with exactly what the People TAB rendered before it
     * became a board, and nothing a rep had is taken away.
     */
    defaults: {
      tekstak: ['org-chart', 'contacts-directory', 'contact-inbox'],
      gas: ['org-chart', 'contacts'],
    },
  },
};

/** The `user_preferences` row a board is stored in. */
export function accountBoardPreferenceType(boardKey: AccountBoardKey): string {
  return boardKey === 'overview' ? 'account_dashboard' : `account_dashboard_${boardKey}`;
}

/** Every preference type a board can occupy — for the backend's validator map. */
export const ACCOUNT_BOARD_PREFERENCE_TYPES: string[] =
  (Object.keys(ACCOUNT_BOARDS) as AccountBoardKey[]).map(accountBoardPreferenceType);

/**
 * The widgets this board's add menu offers: implemented by the host, and in
 * one of the board's groups.
 */
export function accountBoardCatalog(
  host: DashboardHost,
  boardKey: AccountBoardKey,
): AccountWidgetMeta[] {
  const board = ACCOUNT_BOARDS[boardKey] || ACCOUNT_BOARDS.overview;
  const listed = board.types ? new Set<string>(board.types) : null;
  return (Object.keys(ACCOUNT_WIDGET_META) as AccountWidgetType[])
    .map((type) => ACCOUNT_WIDGET_META[type])
    .filter((meta) => meta.hosts.indexOf(host) !== -1)
    .filter((meta) => (listed
      ? listed.has(meta.type)
      : board.groups === null || board.groups.indexOf(meta.group) !== -1));
}

/** A board's first-run arrangement. */
export function defaultAccountBoard(
  host: DashboardHost,
  boardKey: AccountBoardKey,
): AccountDashboardConfig {
  if (boardKey === 'overview') return defaultAccountDashboard(host);
  const board = ACCOUNT_BOARDS[boardKey];
  if (!board) return defaultAccountDashboard(host);
  return makeDefault(host, board.defaults[host] || []);
}

const ORDINAL_ID = /^w([1-9]\d*)$/;
const ordinalId = (n: number) => `w${n}`;

/**
 * A new instance needs a fresh id. Deterministic — no RNG, stable under SSR.
 *
 * ORDINAL, NOT TYPE-DERIVED. The id is the grid key and nothing else — it is
 * never shown, never searched, never joined — and it is paid four times in the
 * stored blob. `purchasing-authority-37` cost 23 bytes where `w37` costs 3, and
 * across a full board that difference was the entire remaining margin against
 * Apps Script's per-property ceiling (see the note on MAX_DASHBOARD_WIDGETS).
 *
 * This is the ONE minter: `AccountBoard.tsx` calls it for both hosts, and the
 * shipped defaults use the same numbering. `type` is accepted for the callers
 * that already pass it and deliberately ignored.
 *
 * Next = max existing ordinal + 1, so a removed widget's number is not reused
 * within a session (react-grid-layout keys a removed and a re-added item apart
 * that way) and a board still carrying legacy ids gets `w1` — they cannot
 * collide, because no catalog type is a bare `w`.
 */
export function nextWidgetId(existing: Array<{ id: string }>, _type?: string): string {
  let max = 0;
  for (const w of existing) {
    const m = ORDINAL_ID.exec(w.id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return ordinalId(max + 1);
}

/**
 * The v5 -> v6 rewrite: every widget renumbered in stored order, and the map
 * from old id to new so the layouts can follow.
 *
 * Stored order, not sorted, so two hosts reading the same blob agree and so
 * the numbering is stable across the local-mirror and server-copy reads that
 * happen back to back on first paint. Runs once per board (version-gated in
 * normalizeAccountDashboard); a v6 board keeps its ids, which is what lets a
 * removed widget's number stay retired.
 */
function renumberWidgetIds(
  widgets: AccountWidgetInstance[],
): { widgets: AccountWidgetInstance[]; idMap: Map<string, string> } {
  const idMap = new Map<string, string>();
  const out = widgets.map((w, i) => {
    const id = ordinalId(i + 1);
    idMap.set(w.id, id);
    return w.id === id ? w : { ...w, id };
  });
  return { widgets: out, idMap };
}

// Hard caps: this blob is client-authored and lands in jsonb / a UserProperty
// (Apps Script caps a single property at 9KB, which is the tighter of the two).
//
// The two caps are sized so the COUNT cap is always the binding one, measured
// against the worst board that is actually reachable: 32 widgets all of the
// longest-named type (ids are type-derived, and an id is paid four times — once
// in `widgets`, once per breakpoint in `layouts`), with a config on the types
// that have settings editors. That lands at ~7.6KB, inside the byte cap, which
// is itself inside Apps Script's 9KB-per-property ceiling.
//
// The count has to stay comfortably ABOVE the catalog size or a rep who adds
// one of everything cannot then add a second News widget. Two things move
// these numbers and BOTH need a re-measure: growing the catalog, and
// lengthening widget type names. The failure it prevents is a board that
// passes the widget check, fails the byte check, and reports "couldn't save"
// without saying which limit it hit. Guard:
// apps/backend/tests/services/account-dashboard-config.test.js.
//
// Re-measured 2026-09-10, when `renewal-runway` and `share-of-wallet` took the
// TekStak catalog to 32 and the old cap of 32 left a rep who added one of
// everything with ZERO spare slots. Worst reachable board is N copies of the
// longest type name (`purchasing-authority`, 20 chars — both new types are
// shorter, so they do not move the ceiling, only the catalog size):
//
//     n=32  7,499 bytes      n=36  8,441 (8,529 with configs)
//     n=38  8,913            n=40  9,385  — over Apps Script's 9,216 ceiling
//
// 36 keeps four spare slots above a 32-widget catalog, and 8,800 keeps the
// COUNT the binding cap (8,529 < 8,800) while staying inside the 9KB
// per-property limit with room to spare.
//
// ─── 2026-09-11: THE CATALOG WAS FULL, AND THE ENCODING WAS WHY ─────────────
// (The two measurements above are history; the encoding changed on 2026-09-13
// and the arithmetic below supersedes them.)
//
// The Agency Site Locations work (S8) had a `locations` widget ready and could
// NOT land it: at a catalog of 34 the cap of 36 left exactly the two spare
// slots `boardRegistry.test.js` requires, so a 35th type broke that guard with
// no cap left to raise:
//
//     type-derived ids:  n=36  8,609 bytes   n=38  9,083   n=39  9,320 — OVER
//                        Apps Script's 9,216-byte per-property ceiling
//
// The blob was dominated by the widget id, type-derived and paid FOUR times —
// once in `widgets`, once per breakpoint in `layouts`.
//
// ─── 2026-09-13: ORDINAL IDS ────────────────────────────────────────────────
// `nextWidgetId` now mints `w1`, `w2`, … (it is the one minter — AccountBoard
// calls it for both hosts, and the defaults use it too), and v5 -> v6 renumbers
// every existing board on read, remapping its layouts. Same worst-case board,
// re-measured — every widget the longest type name, configs at the editor rate:
//
//     ordinal ids:  n=36  5,733 bytes   n=40  6,361   n=48  7,639
//                   n=55  8,760 (the most that fits under the byte cap)
//                   n=57  9,096 (the most that fits under Apps Script at all)
//
// 40 leaves five spare slots above TekStak's 35-widget catalog, the byte cap
// stays where it was and is no longer close to binding (6,361 < 8,800), and
// raising the count again is cheap right up to 55. A LEGACY board is still
// valid on the way in — the backend validator accepts any id up to 80 chars and
// the largest legacy board (36) is 8,609 bytes, under the cap — and comes out
// of normalize renumbered, so it reclaims the space the first time it is
// opened. Guards: `boardRegistry.test.js` (two spare),
// packages/core/test/account-dashboard-caps.test.mjs (this arithmetic, both
// encodings) and account-dashboard-merges.test.mjs (the renumber itself).
//
// ─── 2026-09-17: 40 -> 48 ───────────────────────────────────────────────────
// Three tabs became boards and the catalog grew with them (`contacts-directory`
// for the People board's searchable list), taking TekStak's claim to 37 — one
// spare against the two `boardRegistry.test.js` requires. The next widget would
// have broken the guard, which is the same wall S8 hit, so the cap moves before
// something is waiting on it rather than after.
//
// 48, not 55. 55 is the true maximum under the byte cap and spending it would
// be a mistake: at n=55 a worst-case board is 8,760 bytes against 8,800, which
// is FORTY bytes of margin, and the figure is a function of the longest TYPE
// NAME. `purchasing-authority` is 20 characters and is paid once per widget; a
// type four characters longer adds ~4 bytes x 55 widgets and puts the byte cap
// back in front of the count cap — the exact inversion the caps test exists to
// prevent, arriving via a rename nobody would connect to it. At 48 the same
// board is 7,639 bytes with 1,161 to spare, which survives a much longer name.
//
// The byte cap does NOT move. It is sized against Apps Script's 9,216-byte
// per-property ceiling and there is nothing to gain by crowding it; a board is
// one property per BOARD now (see ACCOUNT_BOARDS), so three boards are three
// properties rather than one blob, and the per-board arithmetic is unchanged.
export const MAX_DASHBOARD_WIDGETS = 48;
export const MAX_DASHBOARD_BYTES = 8800;

const isPos = (v: unknown): v is number => typeof v === 'number' && isFinite(v) && v >= 0;

function isGridItem(v: unknown): v is DashboardGridItem {
  if (!v || typeof v !== 'object') return false;
  const g = v as Partial<DashboardGridItem>;
  return (
    typeof g.i === 'string' && g.i.length > 0 && g.i.length <= 80 &&
    isPos(g.x) && isPos(g.y) &&
    typeof g.w === 'number' && isFinite(g.w) && g.w > 0 && g.w <= 24 &&
    typeof g.h === 'number' && isFinite(g.h) && g.h > 0 && g.h <= 100
  );
}

/**
 * Structural validation, not a vibe check. Every field that reaches the grid
 * or the store is shape- and size-checked; unknown widget `type` strings pass
 * on purpose (see the forward-compatibility invariant above).
 */
export function isAccountDashboardConfig(v: unknown): v is AccountDashboardConfig {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const c = v as Partial<AccountDashboardConfig>;
  if (c.v !== undefined && (typeof c.v !== 'number' || c.v < 1)) return false;
  if (!Array.isArray(c.widgets) || c.widgets.length > MAX_DASHBOARD_WIDGETS) return false;
  for (const w of c.widgets) {
    if (!w || typeof w !== 'object' || Array.isArray(w)) return false;
    const wi = w as Partial<AccountWidgetInstance>;
    if (typeof wi.id !== 'string' || wi.id.length === 0 || wi.id.length > 80) return false;
    if (typeof wi.type !== 'string' || wi.type.length === 0 || wi.type.length > 80) return false;
    if (wi.config !== undefined &&
        (typeof wi.config !== 'object' || wi.config === null || Array.isArray(wi.config))) return false;
  }
  const ids = new Set(c.widgets.map((w) => (w as AccountWidgetInstance).id));
  if (ids.size !== c.widgets.length) return false; // duplicate ids break grid keys
  if (!c.layouts || typeof c.layouts !== 'object' || Array.isArray(c.layouts)) return false;
  for (const bp of DASHBOARD_BREAKPOINT_KEYS) {
    const items = (c.layouts as DashboardLayouts)[bp];
    if (items === undefined) continue;
    if (!Array.isArray(items) || items.length > MAX_DASHBOARD_WIDGETS) return false;
    if (!items.every(isGridItem)) return false;
  }
  return true;
}

/**
 * Coerce anything — the wire, a UserProperty string, an older build's blob —
 * into a board that renders. Never throws: a corrupt layout degrades to the
 * host default, which is always usable.
 *
 * Also guarantees every widget owns a layout item at every breakpoint. Configs
 * drift (a widget added by a build that only knew about `lg`, a hand-edited
 * row); missing items are placed at the bottom at their default size rather
 * than silently vanishing from a narrow window.
 */
export function normalizeAccountDashboard(
  raw: unknown,
  host: DashboardHost,
): AccountDashboardConfig {
  let value: unknown = raw;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return defaultAccountDashboard(host);
    try {
      value = JSON.parse(text);
    } catch {
      return defaultAccountDashboard(host);
    }
  }
  if (!isAccountDashboardConfig(value)) return defaultAccountDashboard(host);

  const config = value as AccountDashboardConfig;
  // A blob from a FUTURE version is not ours to interpret.
  if ((config.v ?? ACCOUNT_DASHBOARD_VERSION) > ACCOUNT_DASHBOARD_VERSION) {
    return defaultAccountDashboard(host);
  }

  // Every height migration this board has not seen yet. A widget raised by
  // more than one of them lands on the same number either way — the target is
  // always the catalog default, never an increment.
  const storedVersion = config.v ?? 1;

  // v3 -> v4 runs FIRST, because every step below reads widget types: the
  // height migrations look up each widget's catalog meta, and an absorbed type
  // has no meta to find. Rewriting first means a merged widget is raised by
  // its SURVIVOR's default, which is the height it will actually render at.
  // Every merge, not just v4's. Re-running an applied merge is a no-op — the
  // absorbed type is already gone from the board — so gating on "below current"
  // means a later bump needs a TYPE_MERGES entry and nothing else.
  const merged = storedVersion < ACCOUNT_DASHBOARD_VERSION
    ? applyTypeMerges(config.widgets, host)
    : config.widgets;

  // v5 -> v6 runs after the merges (so a dropped duplicate is never numbered)
  // and before anything keyed by id below. `idMap` carries the old ids to the
  // layout pass: a layout entry that named `purchasing-authority-3` now has to
  // name `w4`, or the widget is placed at the bottom as if it were new.
  const { widgets, idMap } = storedVersion < ORDINAL_ID_VERSION
    ? renumberWidgetIds(merged)
    : { widgets: merged, idMap: null as Map<string, string> | null };

  const raiseTo = new Map<string, number>();
  for (const migration of HEIGHT_MIGRATIONS) {
    if (storedVersion >= migration.version) continue;
    for (const widget of widgets) {
      const meta = widgetMeta(widget.type);
      if (meta && migration.applies(meta)) raiseTo.set(widget.id, meta.defaultSize.h);
    }
  }

  const layouts: DashboardLayouts = {};
  for (const bp of DASHBOARD_BREAKPOINT_KEYS) {
    const cols = DASHBOARD_COLS[bp];
    const items = (config.layouts[bp] ?? [])
      .map((it) => (idMap && idMap.has(it.i) ? { ...it, i: idMap.get(it.i) as string } : it))
      .filter((it) => widgets.some((w) => w.id === it.i))
      .map((it) => {
        const floor = raiseTo.get(it.i);
        return floor !== undefined && it.h < floor ? { ...it, h: floor } : it;
      });
    const have = new Set(items.map((it) => it.i));
    let bottom = items.reduce((m, it) => Math.max(m, it.y + it.h), 0);
    for (const widget of widgets) {
      if (have.has(widget.id)) continue;
      const meta = widgetMeta(widget.type);
      const size = meta ? meta.defaultSize : { w: 6, h: 8 };
      items.push({ i: widget.id, x: 0, y: bottom, w: Math.min(size.w, cols), h: size.h });
      bottom += size.h;
    }
    // A layout saved at a wider breakpoint can carry a span past this grid.
    layouts[bp] = items.map((it) => ({
      ...it,
      w: Math.min(it.w, cols),
      x: Math.min(it.x, Math.max(0, cols - Math.min(it.w, cols))),
    }));
  }

  return { v: ACCOUNT_DASHBOARD_VERSION, widgets, layouts };
}

/** True when the user has arranged anything at all. */
export function hasDashboardCustomization(
  config: AccountDashboardConfig | null | undefined,
): boolean {
  return !!config && Array.isArray(config.widgets) && config.widgets.length > 0;
}
